<template>
  <nav v-if="state.showSkipLink" aria-labelledby="skip-to-content-link">
    <a id="skip-to-content-link" class="skip-link no-underline var-dark" href="#content">
      <span class="skip-link__text">{{ $t('skip-to-content') }}</span>
    </a>
  </nav>
  <header id="top" class="site-wrapper site-wrapper--header" tabindex="-1">
    <placeholder
      v-if="headerPlaceholder"
      :key="state.headerKey"
      name="jss-header"
      :rendering="headerPlaceholder"
    />
    <breadcrumbs-bar v-if="breadcrumbsBarFields.fields" :fields="breadcrumbsBarFields.fields" />
  </header>
  <div v-if="state.loading" class="loading-wrapper">
    <loading-spinner />
  </div>
  <layout v-else :route="jssStore.validatedRouteData" />
  <footer class="site-wrapper site-wrapper--footer">
    <related-content v-if="relatedContentFields.fields" :fields="relatedContentFields.fields" />
    <placeholder
      v-if="footerPlaceholder"
      :key="state.footerKey"
      name="jss-footer"
      :rendering="footerPlaceholder"
    />
    <back-to-top />
  </footer>
  <route-announcer :is-route-loading="state.loading" :title="routeTitle" />
  <guestline-link />
</template>

<script setup lang="ts">
import { watch, onBeforeMount, reactive, watchEffect, defineAsyncComponent, computed } from 'vue';
import type {
  ComponentRendering,
  HtmlElementRendering,
  LayoutServiceData,
} from '@sitecore-jss/sitecore-jss/layout';
import { isEditorActive, isServer } from '@sitecore-jss/sitecore-jss/utils';
import { Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { useHead } from '@unhead/vue';
import { useRouter } from 'vue-router';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { RedirectTarget } from './types';
import { getLayoutServiceRedirect, isLayoutServiceData } from './utils/layoutServiceRedirect';
import { layoutServiceFactory } from './lib/layout-service-factory';
import { useSpaPageViewTracking } from './data-layer/hooks/useSpaPageViewTracking';
import config from './temp/config';
import Layout from './Layout.vue';
import LoadingSpinner from './components/2_elements/LoadingSpinner/LoadingSpinner.vue';
import RouteAnnouncer from './RouteAnnouncer.vue';
import BreadcrumbsBar from './components/BreadcrumbsBar/BreadcrumbsBar.vue';
import GuestlineLink from './components/2_elements/GuestlineLink/GuestlineLink.vue';
import RelatedContent from './components/RelatedContent/RelatedContent.vue';
import { STEP_ID } from './components/BookingApp/stores/bookingAppConstants';
import { useJssStore } from './stores/jssStore';

const BackToTop = defineAsyncComponent(
  () =>
    import(/* webpackChunkName: "BackToTop" */ './components/2_elements/BackToTop/BackToTop.vue')
);

const jssStore = useJssStore();
const router = useRouter();
const { trackSpaPageView } = useSpaPageViewTracking();

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static routing isn't enough -
// we need to load dynamic route data from Sitecore when the client side route changes.
// So vue-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

const props = defineProps<{
  route: RouteLocationNormalizedLoaded;
}>();

const state = reactive({
  notFound: true,
  defaultLanguage: config.defaultLanguage,
  loading: true,
  headerKey: '',
  footerKey: '',
  showSkipLink: false,
});

const isComponentRendering = (
  rendering: ComponentRendering | HtmlElementRendering
): rendering is ComponentRendering => 'componentName' in rendering;

const routeTitle = computed(() => {
  const metaTitle = jssStore.validatedRouteData?.fields?.metaTitle;
  return metaTitle && 'value' in metaTitle && typeof metaTitle.value === 'string'
    ? metaTitle.value
    : undefined;
});

const showSkipLink = () =>
  !!jssStore.validatedRouteData?.placeholders['jss-header']?.find(
    (component) => isComponentRendering(component) && component.componentName === 'MainHeader'
  );

// if the app state has routeData, we don't need to load it and don't need a loading screen
if (jssStore.routeData) {
  state.loading = false;
  state.showSkipLink = showSkipLink();
}

// route path from vue router - if route was resolved, it's not a 404
if (props.route !== null) {
  state.notFound = false;
}

// if we have an initial SSR state, and that state doesn't have a valid route data,
// then this is a 404 route.
if (!jssStore.routeData) {
  state.notFound = true;
}

// if we have initial context data, and that context data has a language defined, set the default language
// (this makes the language of content follow the Sitecore context language cookie)
// note that a route-based language (i.e. /de-DE) will override this default; this is for home.
if (jssStore.language) {
  state.defaultLanguage = jssStore.language;
}

const headerPlaceholder = computed<ComponentRendering & { key: string }>(() => ({
  componentName: 'RouteHandler',
  key: state.headerKey,
  placeholders: {
    'jss-header':
      jssStore.validatedRouteData?.placeholders?.['jss-header']?.filter(
        (component) =>
          !isComponentRendering(component) || component.componentName !== 'BreadcrumbsBar'
      ) || [],
  },
}));

const footerPlaceholder = computed<ComponentRendering & { key: string }>(() => ({
  componentName: 'RouteHandler',
  key: state.footerKey,
  placeholders: {
    'jss-footer':
      jssStore.validatedRouteData?.placeholders?.['jss-footer']?.filter(
        (component) =>
          !isComponentRendering(component) || component.componentName !== 'RelatedContent'
      ) || [],
  },
}));

const breadcrumbsBarFields = computed(() => ({
  key: state.loading,
  fields: jssStore.validatedRouteData?.placeholders?.['jss-header']?.find(
    (component): component is ComponentRendering =>
      isComponentRendering(component) && component.componentName === 'BreadcrumbsBar'
  )?.fields,
}));

const relatedContentFields = computed(() => ({
  fields: jssStore.validatedRouteData?.placeholders?.['jss-footer']?.find(
    (component): component is ComponentRendering =>
      isComponentRendering(component) && component.componentName === 'RelatedContent'
  )?.fields,
}));

const preconnectOrigins = [{ href: 'https://consent.cookiebot.com' }].map(
  (origin) =>
    origin && {
      ...origin,
      rel: 'preconnect',
    }
);

const bodyAttrs = computed(() => ({
  id: jssStore.brandName,
  ...(jssStore.theme && { class: [jssStore.theme] }),
}));

if (isServer()) {
  useHead({
    link: [
      {
        rel: 'dns-prefetch',
        href: 'https://sdk.woosmap.com',
      },
      ...preconnectOrigins,
    ],
    bodyAttrs: bodyAttrs.value,
  });
}

const bookingStepsIds = Object.values(STEP_ID);

const getSitecoreRouteSegments = (route: RouteLocationNormalizedLoaded) => {
  const segments = route.params.sitecoreRoute;
  if (Array.isArray(segments)) return segments;
  return segments ? [segments] : [];
};

const isBookingAppRoute = (route: RouteLocationNormalizedLoaded) =>
  !!jssStore.bookingAppUrl && route.path?.startsWith(jssStore.bookingAppUrl);

// watch for a change in the 'route' prop
watch(
  () => props.route,
  (newRoute, oldRoute) => {
    // if the route contains a hash value, assume the URL is a named anchor/bookmark link, e.g. /page#anchorId.
    // in that scenario, we don't want to fetch new route data but instead allow default browser behavior.
    if (newRoute.hash !== '' && newRoute.path === oldRoute.path) {
      return;
    }
    // if in Sitecore editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isEditorActive()) {
      if (newRoute.path !== oldRoute.path) {
        window.location.assign(newRoute.path);
      }
      return;
    }
    // if the route contains book a space widget and a number parameter e.g. /book/4
    const routeWithBooking = jssStore.routeData?.placeholders['jss-main']?.some(
      (component) =>
        isComponentRendering(component) && component.componentName === 'BookSpaceWidget'
    );

    const bookingRouteWithParams = (route: RouteLocationNormalizedLoaded) => {
      const routeSegments = getSitecoreRouteSegments(route);
      return (
        routeWithBooking &&
        !!routeSegments.length &&
        ['2', '3', '4', '5', '6', '7', '8', '9', '10'].includes(routeSegments.at(-1) || '')
      );
    };

    const newRouteSegments = getSitecoreRouteSegments(newRoute);
    const oldRouteSegments = getSitecoreRouteSegments(oldRoute);

    if (bookingRouteWithParams(newRoute)) {
      // overwrite undefined state from BookASpace widget with necessery properties
      history.replaceState(
        {
          key: history.state?.key ?? null,
          back: oldRoute.path,
          current: `/${newRouteSegments.slice(0, -1).join('/')}`,
        },
        ''
      );

      if (newRoute.path === oldRoute.path) {
        window.location.href = `${window.location.origin}/${newRouteSegments
          .slice(0, -1)
          .join('/')}`;
      }
      return;
    }

    if (bookingRouteWithParams(oldRoute)) {
      updateRouteData(`${window.location.origin}/${oldRouteSegments.slice(0, -1).join('/')}`);
      return;
    }

    if (isBookingAppRoute(newRoute) && isBookingAppRoute(oldRoute)) {
      // Both routes are within booking app - ignore the change
      return;
    }

    // Declarative map of components whose query-string changes should NOT trigger a layoutService fetch
    const QUERY_PARAM_GUARDS = [
      {
        componentName: 'IOrderMenu',
        queryParams: ['type', 'section'],
      },
      {
        componentName: 'RegularEventsList',
        queryParams: ['month', 'category', 'eventId'],
      },
      {
        componentName: 'SportEventList',
        queryParams: ['month', 'category', 'tournament'],
      },
      {
        componentName: 'VenueFinder',
        queryParams: ['q', 'page', 'brand', 'facilities', 'variant'],
      },
      {
        componentName: 'PubSearch',
        queryParams: [
          'q',
          'page',
          'brand',
          'facilities',
          'agreement-type',
          'trading-style',
          'location-type',
          'variant',
        ],
      },
    ];

    const hasQueryParamGuard = QUERY_PARAM_GUARDS.some(({ componentName, queryParams }) => {
      const componentOnRoute = jssStore.routeData?.placeholders['jss-main']?.some(
        (component) => isComponentRendering(component) && component.componentName === componentName
      );

      return (
        componentOnRoute &&
        newRoute.path === oldRoute.path &&
        queryParams.some((param) => newRoute.query[param] || oldRoute.query[param])
      );
    });

    if (hasQueryParamGuard) return;

    updateRouteData(`${window.location.origin}${oldRoute.path}`);
  }
);

// watch for not found state changes, if the route is not found - reload the page completely
watchEffect(() => {
  if (state.notFound && !state.loading) {
    window.location.reload();
  }
});

onBeforeMount(() => {
  // if no existing routeData is present (from SSR), get Layout Service fetching the route data
  if (!jssStore.routeData) {
    updateRouteData();
  }
});

const getSitecoreRoutePath = () => {
  const sitecoreRouteSegments = getSitecoreRouteSegments(props.route);
  let sitecoreRoutePath = sitecoreRouteSegments.length ? sitecoreRouteSegments.join('/') : '/';

  if (!sitecoreRoutePath.startsWith('/')) {
    sitecoreRoutePath = `/${sitecoreRoutePath}`;
  }

  if (
    jssStore.bookingAppUrl &&
    sitecoreRoutePath.includes(jssStore.bookingAppUrl) &&
    bookingStepsIds.includes(sitecoreRouteSegments.at(-1))
  ) {
    // Remove the last segment (step) from the route path
    const pathWithoutStep = sitecoreRouteSegments.slice(0, -1).join('/');
    return pathWithoutStep.startsWith('/') ? pathWithoutStep : `/${pathWithoutStep}`;
  }

  return sitecoreRoutePath;
};

const handleInvalidRedirect = (redirectUrl: unknown, error?: unknown) => {
  console.warn('Redirect URL is not valid:', redirectUrl, error);
  state.notFound = true;
  state.loading = false;
};

const handleRedirect = (redirect: RedirectTarget) => {
  if (redirect.type === 'invalid') {
    handleInvalidRedirect(redirect.url);
    return;
  }

  if (redirect.type === 'external') {
    window.location.replace(redirect.url);
    return;
  }

  void router.replace(redirect.url);
};

/**
 * Loads route data from Sitecore Layout Service into appState.routeData
 */
const updateRouteData = (oldPath?: string) => {
  const sitecoreRoutePath = getSitecoreRoutePath();
  const routeLanguage = props.route.params.lang;
  const language =
    (Array.isArray(routeLanguage) ? routeLanguage[0] : routeLanguage) ||
    jssStore.language ||
    state.defaultLanguage;
  state.loading = true;

  const apiHost = jssStore.venueOrigin;
  const siteName = jssStore.site?.name;

  // instantiate layout service
  const layoutServiceInstance = layoutServiceFactory.create(apiHost, siteName);
  // get the route data for the new route
  layoutServiceInstance
    .fetchLayoutData(sitecoreRoutePath, language)
    .then((routeData: unknown) => {
      const redirect = getLayoutServiceRedirect(routeData);
      if (redirect) {
        handleRedirect(redirect);
        return;
      }
      if (!isLayoutServiceData(routeData)) {
        handleInvalidRedirect(undefined);
        return;
      }
      if (props.route?.path !== sitecoreRoutePath && !isBookingAppRoute(props.route)) return;
      if (routeData?.sitecore?.route) {
        // Update the JSS store with the fetched data.
        jssStore.patchState(routeData.sitecore.context, routeData.sitecore?.route);
        state.notFound = false;
        state.showSkipLink = showSkipLink();

        document.body.id = jssStore.brandName;
        if (jssStore.theme) document.body.classList.add(jssStore.theme);
        if (oldPath) registerAction(oldPath, routeData);
      } else {
        jssStore.patchState(routeData.sitecore.context, routeData.sitecore?.route);
        state.notFound = true;
      }
      state.loading = false;
    })
    .catch((error) => console.warn(error));
};

/**
 * Data layer action send after changing the page
 */
const registerAction = (oldPath: string, newData: LayoutServiceData) => {
  const metaTitle = newData.sitecore.route?.fields?.metaTitle;
  const title = metaTitle && 'value' in metaTitle ? metaTitle.value : undefined;
  const newPath = newData.sitecore.context?.canonicalUrl;

  if (title && newPath && oldPath) {
    trackSpaPageView({
      page_category: jssStore.routeName?.toLowerCase() || '',
      top_level_page_category: jssStore.topLevelPageCategory?.toLowerCase() || '',
      // star_rating_band: '', // New field (TD: provide by backend - CI)
    });
  }
};

function updateKeys() {
  const getDataSource = (placeholderKey: string, componentNames: string | string[]) => {
    const component = jssStore.routeData?.placeholders?.[placeholderKey]?.find(
      (rendering): rendering is ComponentRendering =>
        isComponentRendering(rendering) &&
        (Array.isArray(componentNames)
          ? componentNames.includes(rendering.componentName)
          : rendering.componentName === componentNames)
    );
    return component?.dataSource ?? 'no-data';
  };

  const allFooterDataSources = jssStore.routeData?.placeholders?.['jss-footer']
    ?.map((component) =>
      isComponentRendering(component) ? component.dataSource || 'no-data' : 'no-data'
    )
    .join('|');

  state.headerKey =
    getDataSource('jss-header', ['MainHeader', 'ThreeLevelMainHeader']) + jssStore.isBookingAppPage;

  state.footerKey = allFooterDataSources;
}

watchEffect(() => {
  updateKeys();
});
</script>
